import { useState } from "react"
import ReactMarkdown from "react-markdown"
import styled from "styled-components"

import { ConnectContent } from "src/components/Integrations/IntegrationConnectWizard/ConnectContent"
import { usePostIntegrationConsentConfirmedEvent } from "src/data/analytics/queries/integrationAnalyticsQueries"
import {
  getIntegrationDialogBody,
  translateConnectionErrorKey,
} from "src/data/integrations/logic/integrations"
import {
  useConnectOauth,
  useFetchActiveCredentials,
  usePostClientCredentials,
  usePostIntegrationMultiSecret,
  usePostIntegrationSecret,
} from "src/data/integrations/queries/integrationAuthQueries"
import {
  INTEGRATION_SECRET_DEFAULT,
  OwnerType,
  TIntegration,
  TPostIntegrationConnectionErrorKey,
} from "src/data/integrations/types/integrationTypes"
import { useTranslate } from "src/i18n/useTranslate"
import { Routes } from "src/router/routes"
import { MLink } from "src/ui/Link/MLink"
import { DialogWizard } from "src/ui/Wizard/DialogWizard"
import { IDialogWizardStep } from "src/ui/Wizard/wizardTypes"
import { ErrorService } from "src/utils/ErrorService"

export function IntegrationConnectWizard({
  integration,
  orgId,
  onClose,
  open,
  reauthorizationAccountId,
}: {
  integration: TIntegration
  orgId: string
  onClose: () => void
  open: boolean
  reauthorizationAccountId?: string
}) {
  const reauthorizationRequired = !!reauthorizationAccountId
  const { t, langKeys } = useTranslate()
  const [currentStep, setCurrentStep] = useState(0)
  const [windowLoading, setWindowLoading] = useState(false)
  const [secret, setSecret] = useState("")
  const [clientId, setClientId] = useState("")
  const [error, setError] = useState("")

  const connectOauth = useConnectOauth({ ownerType: OwnerType.ORGANIZATION })

  const [multiSecret, setMultiSecret] = useState<Map<string, string>>(
    new Map(
      integration.auth.multi_secret?.map((mSecret) => [
        mSecret.id ?? INTEGRATION_SECRET_DEFAULT,
        "",
      ])
    )
  )

  const fetchActiveCredentials = useFetchActiveCredentials({
    ownerId: orgId,
    ownerType: OwnerType.ORGANIZATION,
    integration: integration.integration_identifier,
    filters: { limit: 1, offset: 0 },
  })
  const isMultiAccountIntegration =
    (fetchActiveCredentials.data?.paging.total_count ?? 0) > 0

  const settingsTabLocation = Routes.OrgIntegrationSettingsTab.location(
    integration.integration_identifier
  )

  const homeTabLocation = Routes.OrgIntegration.location(
    integration.integration_identifier
  )

  const integrationRedirectPath = isMultiAccountIntegration
    ? `${settingsTabLocation.pathname}#${settingsTabLocation.hash}`
    : homeTabLocation.pathname
  const redirectUrl = window.location.origin + integrationRedirectPath

  const postClientCredentials = usePostClientCredentials({
    ownerType: OwnerType.ORGANIZATION,
  })

  const postIntegrationSecret = usePostIntegrationSecret({
    ownerType: OwnerType.ORGANIZATION,
  })

  const postIntegrationMultiSecret = usePostIntegrationMultiSecret({
    ownerType: OwnerType.ORGANIZATION,
  })

  const postIntegrationConsentConfirmedEvent =
    usePostIntegrationConsentConfirmedEvent()

  const isStepLoading =
    windowLoading ||
    postIntegrationMultiSecret.isLoading ||
    postIntegrationSecret.isLoading ||
    postClientCredentials.isLoading ||
    connectOauth.isLoading

  function handleClose() {
    onClose()
    setCurrentStep(0)
    setError("")
  }

  function handleSuccess() {
    window.location.reload()
    onClose()
  }

  function handleError(errorKey: TPostIntegrationConnectionErrorKey) {
    setError(
      translateConnectionErrorKey({
        t,
        errorKey,
        integration,
      })
    )
    setWindowLoading(false)
  }

  function handleConnectSubmit(type: TIntegration["auth"]["type"]) {
    setWindowLoading(true)

    const baseParams = {
      orgId,
      integration: integration.integration_identifier,
      reauthorize: reauthorizationRequired,
    }

    switch (type) {
      case "SECRET":
        if (!orgId) {
          ErrorService.captureMessage("No orgId")
          setError(t(langKeys.failed_something_went_wrong))
          return
        }
        postIntegrationSecret.mutate(
          { ...baseParams, secret },
          {
            onSuccess: handleSuccess,
            onError: (error) => handleError(error.response?.data.error_key),
          }
        )
        break
      case "MULTI_SECRET":
        if (!orgId) {
          ErrorService.captureMessage("No orgId")
          return
        }

        postIntegrationMultiSecret.mutate(
          {
            ...baseParams,
            secrets: Array.from(multiSecret).map(([id, secret]) => ({
              id,
              secret,
            })),
          },
          {
            onSuccess: handleSuccess,
            onError: (error) => handleError(error.response?.data.error_key),
          }
        )
        break
      case "OAUTH2_CLIENT_CREDENTIALS":
        postClientCredentials.mutate(
          {
            ...baseParams,
            clientSecret: secret,
            clientId,
          },
          {
            onSuccess: handleSuccess,
            onError: (error) => handleError(error.response?.data.error_key),
          }
        )
        break
      default:
        break
    }
  }

  function startOauthFlow(url?: string) {
    setWindowLoading(true)

    connectOauth.mutate(
      {
        orgId,
        redirectUrl: redirectUrl,
        integration: integration.integration_identifier,
        reauthorizeAccountId: reauthorizationAccountId,
        url,
      },
      {
        onSuccess: () => {
          setWindowLoading(false)
          onClose()
        },
      }
    )
  }

  function handleAgreePrivacy() {
    postIntegrationConsentConfirmedEvent.mutate({
      integration_name: integration.name,
      reconnect: reauthorizationRequired,
    })

    if (integration.auth.type === "OAUTH2") {
      return startOauthFlow()
    }
    if (integration.auth.type === "SEAM_CONNECT_WEBVIEW") {
      return startOauthFlow("/integrations/auth/seam_connect_webviews/connect")
    }

    return setCurrentStep(currentStep + 1)
  }

  const steps: IDialogWizardStep[] = [
    {
      title: t(langKeys.integration_dialog_accept_title, {
        name: integration.name,
      }),
      nextButtonLabel: t(langKeys.integrations_dialog_agree_and_continue),

      onNext: handleAgreePrivacy,
      onBack: handleClose,
      loading: isStepLoading,
      component: (
        <Body>
          {getIntegrationDialogBody({
            integrationId: integration.integration_identifier,
            name: integration.name,
            type: integration.type,
            t,
          })}
        </Body>
      ),
    },
    {
      title: t(langKeys.connect),
      description: (
        <ReactMarkdown
          components={{
            a: ({ href, children }) => (
              <MLink color="unset" href={href ?? ""}>
                {children as React.ReactNode}
              </MLink>
            ),
          }}
        >
          {integration.auth.description ?? ""}
        </ReactMarkdown>
      ),
      loading: isStepLoading,
      nextButtonLabel: t(langKeys.connect),
      backButtonLabel: t(langKeys.back),
      onBack: () => setCurrentStep((prev) => prev - 1),
      onNext: () => handleConnectSubmit(integration.auth.type),
      component: (
        <ConnectContent
          integration={integration}
          secret={secret}
          setSecret={setSecret}
          multiSecret={multiSecret}
          setMultiSecret={setMultiSecret}
          clientId={clientId}
          setClientId={setClientId}
          postIntegrationSecret={postIntegrationSecret}
          postIntegrationMultiSecret={postIntegrationMultiSecret}
          postClientCredentials={postClientCredentials}
        />
      ),
    },
  ]

  return (
    <DialogWizard
      open={open}
      steps={steps}
      currentStep={currentStep}
      onBack={() => {
        onClose()
        setCurrentStep(0)
        setError("")
      }}
      error={error}
      loading={isStepLoading}
    />
  )
}

const Body = styled.div`
  white-space: pre-line;
`
